import { useState, useEffect } from 'react';
import { Stack, Box, IconButton } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SortIcon from '@mui/icons-material/Sort';
import { SearchToolbar } from './SearchToolbar.js';
import { SortingButtons } from './SortingButtons.js';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { getJwtPayload } from '../../utilities/getJwtPayload';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation } from "react-router";
import AddRowButton from './AddRowButton';


export function StationsPageHeader({ topPadding, sortConfig, sortRows, search, setSearch, onAddRow }) {
  const { getAccessTokenSilently } = useAuth0();
  const [userCanEdit, setUserCanEdit] = useState(false);

  useEffect(() => {
      getAccessTokenSilently()
          .then(token => {
              const payload = getJwtPayload(token);
              setUserCanEdit(payload.permissions?.includes("write:channels"));
          })
          .catch(() => setUserCanEdit(false));
  }, [getAccessTokenSilently]);

  const location = useLocation();
  const isStationsPage = location.pathname === "/stationspage";

  const theme = useTheme();
  const isDesktop = useMediaQuery('(min-width:1010px)');
  const headerBackground = theme.palette.background.default;

  return isDesktop ? (
    <DesktopHeader
      topPadding={topPadding}
      headerBackground={headerBackground}
      sortConfig={sortConfig}
      sortRows={sortRows}
      search={search}
      setSearch={setSearch}
      userCanEdit={userCanEdit}
      isStationsPage={isStationsPage}
      onAddRow={onAddRow}
    />
  ) : (
    <MobileHeader
      topPadding={topPadding}
      headerBackground={headerBackground}
      sortConfig={sortConfig}
      sortRows={sortRows}
      search={search}
      setSearch={setSearch}
    />
  );
}

const DesktopHeader = ({ topPadding, headerBackground, sortConfig, sortRows, search, setSearch, userCanEdit, isStationsPage, onAddRow }) => (
  <Box sx={{ width: '100%', backgroundColor: headerBackground }}>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        paddingBottom: topPadding,
        paddingTop: topPadding,
        px: 2,
      }}
    >
      {/* Left: SortingButtons */}
      <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-start' }}>
        <SortingButtons sortConfig={sortConfig} sortRows={sortRows} />
      </Box>
      {/* Center: SearchToolbar */}
      <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
        <SearchToolbar search={search} setSearch={setSearch} />
      </Box>
      {/* Right: AddRowButton */}
      <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
        {userCanEdit && isStationsPage && <AddRowButton onClick={onAddRow} />}
      </Box>
    </Box>
  </Box>
);

const MobileHeader = ({ topPadding, headerBackground, sortConfig, sortRows, search, setSearch }) => {
  const [openPanel, setOpenPanel] = useState(null);
  const togglePanel = (panel) => {
    setOpenPanel((prev) => (prev === panel ? null : panel));
  };

  return (
    <Box sx={{ width: '100%', backgroundColor: headerBackground }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ paddingBottom: topPadding, paddingTop: topPadding, px: 2 }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <IconButton onClick={() => togglePanel('sorting')} size="small">
            <SortIcon />
          </IconButton>
          {openPanel === 'sorting' && <SortingButtons sortConfig={sortConfig} sortRows={sortRows} />}
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          {openPanel === 'search' && <SearchToolbar search={search} setSearch={setSearch} />}
          <IconButton onClick={() => togglePanel('search')} size="small">
            <SearchIcon />
          </IconButton>
        </Stack>
      </Stack>
    </Box>
  );
};